import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getAll,
  addCategory as svcAdd,
  updateCategory as svcUpdate,
  deleteCategory as svcDelete,
  reorderCategories as svcReorder,
  syncCategoriesFromBackend,
} from '../services/categoryService';
import { getAdminToken } from '../services/apiClient';
import { CATEGORIES } from '../utils/constants';

const CategoryContext = createContext();

export const useCategories = () => {
  const ctx = useContext(CategoryContext);
  if (!ctx) throw new Error('useCategories must be used within CategoryProvider');
  return ctx;
};

const getFallback = () =>
  CATEGORIES.map((cat, i) => ({
    ...cat,
    id: cat.slug,
    isActive: true,
    showOnHomepage: true,
    order: i,
    isDefault: true,
  }));

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState(() => {
    try {
      return getAll();
    } catch {
      return getFallback();
    }
  });

  // On mount: if admin is logged in, sync categories from backend
  useEffect(() => {
    if (!getAdminToken()) return;
    syncCategoriesFromBackend()
      .then(cats => {
        if (cats && cats.length > 0) setCategories(cats);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(() => {
    try {
      setCategories(getAll());
    } catch {
      setCategories(getFallback());
    }
  }, []);

  const addCategory = useCallback(
    (data) => {
      const cat = svcAdd(data);
      refresh();
      return cat;
    },
    [refresh]
  );

  const updateCategory = useCallback(
    (id, updates) => {
      svcUpdate(id, updates);
      refresh();
    },
    [refresh]
  );

  const deleteCategory = useCallback(
    (id) => {
      const ok = svcDelete(id);
      if (ok) refresh();
      return ok;
    },
    [refresh]
  );

  const reorderCategories = useCallback((ordered) => {
    const withOrder = ordered.map((c, i) => ({ ...c, order: i }));
    svcReorder(withOrder);
    setCategories(withOrder);
  }, []);

  const sorted = [...categories].sort((a, b) => a.order - b.order);
  const activeCategories = sorted.filter((c) => c.isActive);
  const homepageCategories = sorted.filter((c) => c.isActive && c.showOnHomepage);

  return (
    <CategoryContext.Provider
      value={{
        categories: sorted,
        activeCategories,
        homepageCategories,
        addCategory,
        updateCategory,
        deleteCategory,
        reorderCategories,
        refresh,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};
